import { useState } from 'react';
import {
  Title,
  Text,
  Stack,
  Paper,
  Table,
  Button,
  Group,
  ActionIcon,
  Modal,
  TextInput,
  Textarea,
  Badge,
  Skeleton,
  Alert,
  Drawer,
  ThemeIcon,
  Checkbox,
  ColorInput,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconUsers,
  IconAlertTriangle,
  IconDeviceDesktop,
  IconX,
  IconCloudDownload,
  IconCloudUpload,
  IconCloud,
  IconUnlink,
  IconBolt,
} from '@tabler/icons-react';

import {
  useDeviceGroups,
  useGroupDevices,
  useCreateDeviceGroup,
  useUpdateDeviceGroup,
  useDeleteDeviceGroup,
  useRemoveDevicesFromGroup,
  useImportFogGroups,
  useSyncGroupToFog,
  useUnlinkGroupFromFog,
  useWakeOnLanGroup,
  type DeviceGroup,
} from '@/hooks';

export function DeviceGroupsPage() {
  const { data, isLoading, isError } = useDeviceGroups();
  const createGroup = useCreateDeviceGroup();
  const updateGroup = useUpdateDeviceGroup();
  const deleteGroup = useDeleteDeviceGroup();
  const importFogGroups = useImportFogGroups();
  const syncToFog = useSyncGroupToFog();
  const unlinkFromFog = useUnlinkGroupFromFog();
  const wakeOnLanGroup = useWakeOnLanGroup();

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure();
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure();
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure();

  const [editingGroup, setEditingGroup] = useState<DeviceGroup | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<DeviceGroup | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '',
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '',
    });
    setEditingGroup(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    openModal();
  };

  const handleOpenEdit = (group: DeviceGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      color: group.color || '',
    });
    openModal();
  };

  const handleOpenDelete = (group: DeviceGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingGroup(group);
    openDeleteModal();
  };

  const handleOpenDrawer = (group: DeviceGroup) => {
    setSelectedGroupId(group.id);
    openDrawer();
  };

  const handleSubmit = async () => {
    const data = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      color: formData.color || null,
    };

    if (!data.name) {
      notifications.show({
        title: 'Validation Error',
        message: 'Name is required',
        color: 'red',
      });
      return;
    }

    try {
      if (editingGroup) {
        await updateGroup.mutateAsync({ id: editingGroup.id, data });
        notifications.show({
          title: 'Group updated',
          message: `${formData.name} has been updated`,
          color: 'green',
        });
      } else {
        await createGroup.mutateAsync(data);
        notifications.show({
          title: 'Group created',
          message: `${formData.name} has been created`,
          color: 'green',
        });
      }
      closeModal();
      resetForm();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save group',
        color: 'red',
      });
    }
  };

  const handleDelete = async () => {
    if (!deletingGroup) return;

    try {
      await deleteGroup.mutateAsync(deletingGroup.id);
      notifications.show({
        title: 'Group deleted',
        message: `${deletingGroup.name} has been deleted`,
        color: 'green',
      });
      closeDeleteModal();
      setDeletingGroup(null);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete group',
        color: 'red',
      });
    }
  };

  const handleImportFog = async () => {
    try {
      const result = await importFogGroups.mutateAsync();
      if (result.imported > 0) {
        notifications.show({
          title: 'FOG Import Complete',
          message: result.message,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'FOG Import',
          message: result.skipped > 0
            ? 'All FOG groups are already imported'
            : 'No FOG groups found',
          color: 'blue',
        });
      }
      if (result.errors.length > 0) {
        notifications.show({
          title: 'Import Warnings',
          message: result.errors.join(', '),
          color: 'yellow',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Import Failed',
        message: error instanceof Error ? error.message : 'Failed to import FOG groups',
        color: 'red',
      });
    }
  };

  const handleSyncToFog = async (group: DeviceGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await syncToFog.mutateAsync(group.id);
      notifications.show({
        title: 'Synced to FOG',
        message: `${group.name} is now linked to FOG Project`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Sync Failed',
        message: error instanceof Error ? error.message : 'Failed to sync group to FOG',
        color: 'red',
      });
    }
  };

  const handleUnlinkFog = async (group: DeviceGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await unlinkFromFog.mutateAsync(group.id);
      notifications.show({
        title: 'Unlinked from FOG',
        message: `${group.name} is no longer linked to FOG`,
        color: 'blue',
      });
    } catch (error) {
      notifications.show({
        title: 'Unlink Failed',
        message: error instanceof Error ? error.message : 'Failed to unlink group from FOG',
        color: 'red',
      });
    }
  };

  const groups = data?.groups || [];

  return (
    <>
      <Stack gap="lg">
        {/* Page header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Device Groups</Title>
            <Text c="dimmed" size="sm">
              Organize devices for bulk operations
            </Text>
          </div>
          <Group gap="sm">
            <Button
              variant="light"
              leftSection={<IconCloudDownload size={16} />}
              onClick={handleImportFog}
              loading={importFogGroups.isPending}
            >
              Import from FOG
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
              Create Group
            </Button>
          </Group>
        </Group>

        {/* Groups table */}
        <Paper withBorder>
          {isLoading ? (
            <Stack p="md" gap="sm">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} height={50} />
              ))}
            </Stack>
          ) : isError ? (
            <Alert color="red" m="md">
              Failed to load device groups
            </Alert>
          ) : groups.length === 0 ? (
            <Stack align="center" p="xl" gap="md">
              <IconUsers size={48} color="gray" />
              <Text c="dimmed">No device groups created</Text>
              <Text size="sm" c="dimmed" maw={400} ta="center">
                Groups help you organize devices for bulk operations like remediation or imaging.
              </Text>
              <Button variant="light" onClick={handleOpenCreate}>
                Create your first group
              </Button>
            </Stack>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Devices</Table.Th>
                  <Table.Th>FOG</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th w={130}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {groups.map((group) => (
                  <Table.Tr
                    key={group.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleOpenDrawer(group)}
                  >
                    <Table.Td>
                      <Group gap="xs">
                        {group.color && (
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: 3,
                              backgroundColor: group.color,
                            }}
                          />
                        )}
                        <Text fw={500}>{group.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed" lineClamp={1}>
                        {group.description || '-'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={group.deviceCount > 0 ? 'blue' : 'gray'}
                        leftSection={<IconDeviceDesktop size={12} />}
                      >
                        {group.deviceCount}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {group.linkedToFog ? (
                        <Tooltip label={`Linked to FOG group #${group.fogGroupId}`}>
                          <Badge
                            variant="light"
                            color="teal"
                            leftSection={<IconCloud size={12} />}
                          >
                            Linked
                          </Badge>
                        </Tooltip>
                      ) : (
                        <Text size="sm" c="dimmed">-</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {formatDate(group.createdAt)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {group.linkedToFog ? (
                          <Tooltip label="Unlink from FOG">
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              color="gray"
                              onClick={(e) => handleUnlinkFog(group, e)}
                              loading={unlinkFromFog.isPending}
                            >
                              <IconUnlink size={16} />
                            </ActionIcon>
                          </Tooltip>
                        ) : (
                          <Tooltip label="Sync to FOG">
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              color="teal"
                              onClick={(e) => handleSyncToFog(group, e)}
                              loading={syncToFog.isPending}
                            >
                              <IconCloudUpload size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip label="Wake all devices">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            color="yellow"
                            onClick={(e) => {
                              e.stopPropagation();
                              wakeOnLanGroup.mutate({ groupId: group.id });
                            }}
                            loading={wakeOnLanGroup.isPending}
                            disabled={group.deviceCount === 0}
                          >
                            <IconBolt size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={(e) => handleOpenEdit(group, e)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="red"
                          onClick={(e) => handleOpenDelete(group, e)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </Stack>

      {/* Create/Edit Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => {
          closeModal();
          resetForm();
        }}
        title={editingGroup ? 'Edit Group' : 'Create Group'}
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="Lab PCs"
            description="Unique name for this group"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <Textarea
            label="Description"
            placeholder="Computers in the main lab..."
            description="Optional description"
            minRows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <ColorInput
            label="Color"
            placeholder="Pick a color"
            description="Optional color for visual identification"
            format="hex"
            swatches={['#3b82f6', '#22c55e', '#eab308', '#ef4444', '#a855f7', '#ec4899', '#14b8a6', '#f97316']}
            value={formData.color}
            onChange={(value) => setFormData({ ...formData, color: value })}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => { closeModal(); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={createGroup.isPending || updateGroup.isPending}
            >
              {editingGroup ? 'Save Changes' : 'Create Group'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => {
          closeDeleteModal();
          setDeletingGroup(null);
        }}
        title="Delete Group"
        size="sm"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
            Are you sure you want to delete <strong>{deletingGroup?.name}</strong>?
            {deletingGroup && deletingGroup.deviceCount > 0 && (
              <Text size="sm" mt="xs">
                This group contains {deletingGroup.deviceCount} device(s). They will be removed from the group but not deleted.
              </Text>
            )}
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { closeDeleteModal(); setDeletingGroup(null); }}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteGroup.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Group Detail Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title="Group Details"
        position="right"
        size="lg"
      >
        {selectedGroupId && (
          <GroupDrawerContent groupId={selectedGroupId} />
        )}
      </Drawer>
    </>
  );
}

interface GroupDrawerContentProps {
  groupId: string;
  onClose: () => void;
}

function GroupDrawerContent({ groupId }: Omit<GroupDrawerContentProps, 'onClose'>) {
  const { data, isLoading } = useGroupDevices(groupId);
  const removeDevices = useRemoveDevicesFromGroup();
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);

  const group = data?.group;
  const devices = data?.devices || [];

  const handleSelectAll = () => {
    if (selectedDevices.length === devices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(devices.map((d) => d.deviceId));
    }
  };

  const handleToggleDevice = (deviceId: string) => {
    setSelectedDevices((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleRemoveSelected = async () => {
    if (selectedDevices.length === 0) return;

    try {
      await removeDevices.mutateAsync({
        groupId,
        deviceIds: selectedDevices,
      });
      notifications.show({
        title: 'Devices removed',
        message: `Removed ${selectedDevices.length} device(s) from group`,
        color: 'green',
      });
      setSelectedDevices([]);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to remove devices',
        color: 'red',
      });
    }
  };

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={100} />
        <Skeleton height={300} />
      </Stack>
    );
  }

  if (!group) {
    return (
      <Alert color="red">
        Group not found
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Group Info */}
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Group gap="sm">
            {group.color && (
              <div
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  backgroundColor: group.color,
                }}
              />
            )}
            <Title order={4}>{group.name}</Title>
          </Group>
          <Badge variant="light">
            {group.deviceCount} device{group.deviceCount !== 1 ? 's' : ''}
          </Badge>
        </Group>
        {group.description && (
          <Text size="sm" c="dimmed">
            {group.description}
          </Text>
        )}
        <Text size="xs" c="dimmed" mt="sm">
          Created by {group.createdBy} on {formatDate(group.createdAt)}
        </Text>
      </Paper>

      {/* Actions */}
      {selectedDevices.length > 0 && (
        <Group gap="sm">
          <Text size="sm" c="dimmed">
            {selectedDevices.length} selected
          </Text>
          <Button
            size="xs"
            color="red"
            variant="light"
            leftSection={<IconX size={14} />}
            onClick={handleRemoveSelected}
            loading={removeDevices.isPending}
          >
            Remove from group
          </Button>
        </Group>
      )}

      {/* Devices List */}
      <Paper withBorder>
        {devices.length === 0 ? (
          <Stack align="center" p="xl" gap="md">
            <ThemeIcon variant="light" size="xl" color="gray">
              <IconDeviceDesktop size={24} />
            </ThemeIcon>
            <Text c="dimmed">No devices in this group</Text>
            <Text size="sm" c="dimmed">
              Add devices from the Devices page
            </Text>
          </Stack>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={40}>
                  <Checkbox
                    checked={selectedDevices.length === devices.length && devices.length > 0}
                    indeterminate={selectedDevices.length > 0 && selectedDevices.length < devices.length}
                    onChange={handleSelectAll}
                  />
                </Table.Th>
                <Table.Th>Hostname</Table.Th>
                <Table.Th>IP Address</Table.Th>
                <Table.Th>Site</Table.Th>
                <Table.Th>Added</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {devices.map((device) => (
                <Table.Tr key={device.deviceId}>
                  <Table.Td>
                    <Checkbox
                      checked={selectedDevices.includes(device.deviceId)}
                      onChange={() => handleToggleDevice(device.deviceId)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        {device.hostname}
                      </Text>
                      {!device.isActive && (
                        <Badge size="xs" color="gray">
                          Inactive
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {device.ipAddress || '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {device.site || '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label={`Added by ${device.addedBy}`}>
                      <Text size="xs" c="dimmed">
                        {formatDate(device.addedAt)}
                      </Text>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
